<template>
  <div
    ref="picker"
    class="wt-area-picker-stage"
    :class="classObj"
    :style="styleObj"
    @mousedown="onMouseDown"
    :tabindex="index"
    @keydown="move"
  >
    <div
      v-if="debug"
      class="-debug"
      :style="{ color: 'white', position: 'absolute' }"
    >
      {{ debugString }}
    </div>
    <div class="shape" v-show="isActive">
      <span v-if="area.showLabel">{{ area.label }}</span>
    </div>
  </div>
</template>

<script>
/**
 *  Asset Editor's OverlaysFieldContent
 * */
import app from 'app';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import {
  hasActiveControl,
  isFieldShown,
} from 'AssetEditor/functionality/CustomOverlays/objectTrackingApi';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

export default {
  props: ['area', 'index', 'maxAreaZIndex'],

  data() {
    return {
      debug: false,
      mediaUrl: null,
      zIndex: this.area.zIndex,
      offsetX: this.area.offsetX,
      offsetY: this.area.offsetY,
      width: this.area.width,
      height: this.area.height,
    };
  },

  created() {
    this.mediaUrl = this.area.mediaUrl;
    bus.$on('selectedMediaCo', this.onParentValueChanged);
  },

  beforeDestroy() {
    bus.$off('selectedMediaCo', this.onParentValueChanged);
  },

  computed: {
    ...mapGetters('assetEditor', ['pageTmpState', 'currentPage']),
    ...mapState('assetEditor', ['stp']),

    debugString() {
      let res = '';
      res += app.sfp(this.offsetX, 1e3);
      res += ' ';
      res += app.sfp(this.offsetY, 1e3);
      res += ' ';
      res += app.sfp(this.width, 1e3);
      res += ' ';
      res += app.sfp(this.height, 1e3);
      return res;
    },

    /**
     * Driven by the toggle on the field's row in the left panel. Fields with no
     * toggle have no active state and always read as shown.
     */
    isActive() {
      return isFieldShown(this.area);
    },

    classObj() {
      return {
        '-inactive': !this.isActive,
        'shape-rect':
          this.area.shape === 'RECT' || this.area.shape === 'TRANSPARENT_RECT',
        'shape-circle':
          this.area.shape === 'CIRC' || this.area.shape === 'TRANSPARENT_CIRC',
        transparent:
          this.area.shape === 'TRANSPARENT_RECT' ||
          this.area.shape === 'TRANSPARENT_CIRC',
        active: this.activeOverlay === this.area.name,
        hover: this.pageTmpState.hoveredField === this.area.name,
      };
    },

    maxZ() {
      return this.maxAreaZIndex;
    },

    activeOverlay() {
      return this.pageTmpState.activeOverlay;
    },

    leftPx() {
      return Math.round(this.offsetX * app.ASSET_EDITOR_WIDTH);
    },

    topPx() {
      return Math.round(this.offsetY * app.ASSET_EDITOR_HEIGHT);
    },

    widthPx() {
      return Math.round(this.width * app.ASSET_EDITOR_WIDTH);
    },

    heightPx() {
      return Math.round(this.height * app.ASSET_EDITOR_HEIGHT);
    },

    styleObj() {
      let visibility = 'visible';
      if (this.area.hasParent) {
        if (!this.mediaUrl) {
          visibility = 'hidden';
        }
      }
      return {
        left: this.leftPx + 'px',
        top: this.topPx + 'px',
        width: this.widthPx + 'px',
        height: this.heightPx + 'px',
        zIndex: this.zIndex,
        visibility,
      };
    },
  },

  mounted() {
    // an area that starts hidden gets no drag/resize widgets at all
    this.applyActiveState();

    if (_.isEmpty(this.area.value)) {
      const valueChanges = {
        id: this.currentPage.id,
        name: this.area.name,
        type: this.area.type,
        value: this.area.defaultValue,
      };
      // seeded together with the value so a new overlay starts where the preset
      // says. fields without a toggle must not get the key at all
      if (hasActiveControl(this.area)) {
        valueChanges.active = this.isActive;
      }
      this.updateField(valueChanges, false);
    }
  },

  watch: {
    isActive: function () {
      this.applyActiveState();
    },

    activeOverlay: function (value) {
      const maxZ = +this.maxZ;

      if (value !== this.area.name || !this.isActive) {
        return;
      }

      const newZIndex = maxZ + 1;

      const valueChanges = {
        name: this.area.name,
        type: this.area.type,
        value: `${this.offsetX} ${this.offsetY} ${this.width} ${this.height} ${newZIndex}`,
      };
      this.updateField(valueChanges);
      this.zIndex = newZIndex;
    },
  },

  methods: {
    ...mapMutations('assetEditor', ['setTmpPageState']),
    ...mapActions('assetEditor', ['ADD_OR_UPDATE_CUSTOM_OVERLAY_FIELD']),

    initDraggable() {
      const containment = this.$el.closest('.wt-asset-editor-middle-custom');
      $(this.$refs.picker).draggable({
        snap: '.wt-asset-editor-stage',
        snapTolerance: 7,
        scroll: false,
        containment,
        snapMode: 'inner',
        stop: (event, ui) => {
          this.updateDimensions(ui);
        },
      });
    },

    initResizable() {
      $(this.$refs.picker).resizable({
        handles: this.area.forceAspect ? 'ne, se, sw, nw' : 'all',
        aspectRatio: this.area.forceAspect,
        start: (event, ui) => {
          ui.size.width =
            ui.size.width + parseInt($(this.$el).css('border-left-width')) * 2;
          ui.size.height =
            ui.size.height + parseInt($(this.$el).css('border-top-width')) * 2;
        },
        stop: (event, ui) => {
          this.updateDimensions(ui);
        },
      });
    },

    onParentValueChanged({ newValue, oldValue, name }) {
      if (
        this.area.parentFieldName !== name ||
        !this.area.defaultValue ||
        !this.isActive
      ) {
        return;
      }
      this.mediaUrl = newValue.url;
      const newObj = newValue;
      const oldObj = oldValue;
      if (!newObj.url) {
        return;
      }

      const normalizeDimensions = (o) => {
        const parentAr = app.sfp(o.width / o.height, 1e6);
        let [x, y, width, height] = this.area.defaultValue
          .split(' ')
          .map((n) => +n);
        if (parentAr >= 1) {
          const widthPx = width * app.ASSET_EDITOR_WIDTH;
          const heightPx = widthPx / parentAr;
          height = heightPx / app.ASSET_EDITOR_HEIGHT;
        } else {
          const heightPx = height * app.ASSET_EDITOR_HEIGHT;
          const widthPx = heightPx * parentAr;
          width = widthPx / app.ASSET_EDITOR_WIDTH;
        }
        return [x, y, width, height];
      };

      const [x, y, width, height] = normalizeDimensions(newObj);
      if (oldObj.url) {
        const [, , oldWidth, oldHeight] = normalizeDimensions(oldObj);
        const scale = this.width / oldWidth;
        const scale2 = this.height / oldHeight;
        console.log(scale, scale2);
        const newWidth = width * scale;
        const newHeight = height * scale;
        const centerX = +this.offsetX + +this.width / 2;
        const centerY = +this.offsetY + +this.height / 2;
        this.offsetX = centerX - newWidth / 2;
        this.offsetY = centerY - newHeight / 2;
        this.width = newWidth;
        this.height = newHeight;
      } else {
        this.width = width;
        this.height = height;
        this.offsetX = x;
        this.offsetY = y;
      }
      const valueChanges = {
        name: this.area.name,
        type: this.area.type,
        value: `${this.offsetX} ${this.offsetY} ${this.width} ${this.height} ${this.zIndex}`,
      };
      this.updateField(valueChanges);
    },

    /**
     * A hidden area cannot be dragged or resized.
     *
     * The widgets are created and destroyed rather than flipped through their
     * `disabled` option: toggling the option left the box unmovable once it came
     * back, and a fresh widget cannot carry stale state. The geometry lives in
     * the agd, so nothing is lost either way.
     */
    applyActiveState() {
      const $picker = $(this.$refs.picker);

      if (this.isActive) {
        if (!$picker.hasClass('ui-draggable')) {
          this.initDraggable();
        }
        if (!$picker.hasClass('ui-resizable')) {
          this.initResizable();
        }
        return;
      }

      if ($picker.hasClass('ui-draggable')) {
        $picker.draggable('destroy');
      }
      if ($picker.hasClass('ui-resizable')) {
        $picker.resizable('destroy');
      }
    },

    onMouseDown() {
      if (this.stp.status === 'PENDING' || !this.isActive) {
        return;
      }
      this.setTmpPageState({
        activeOverlay: this.area.name,
      });
    },

    updateDimensions(ui) {
      const x = app.sfp(ui.position.left / app.ASSET_EDITOR_WIDTH);
      const y = app.sfp(ui.position.top / app.ASSET_EDITOR_HEIGHT);
      let w;
      let h;
      if (ui.size) {
        w = app.sfp(ui.size.width / app.ASSET_EDITOR_WIDTH);
        h = app.sfp(ui.size.height / app.ASSET_EDITOR_HEIGHT);
      } else {
        w = this.width;
        h = this.height;
      }
      const value = `${x} ${y} ${w} ${h} ${this.zIndex}`;
      const valueChanges = {
        name: this.area.name,
        type: this.area.type,
        value,
      };
      this.offsetX = x;
      this.offsetY = y;
      this.width = w;
      this.height = h;
      this.updateField(valueChanges);
    },

    move(e) {
      const arrowCodes = _.pick($.ui.keyCode, ['UP', 'DOWN', 'LEFT', 'RIGHT']);
      let x = this.leftPx;
      let y = this.topPx;

      if (
        e.target !== e.currentTarget ||
        !this.isActive ||
        !_.values(arrowCodes).includes(e.which)
      ) {
        return;
      }

      switch (e.which) {
        case arrowCodes.UP:
          y = y - 1;
          break;
        case arrowCodes.DOWN:
          y = y + 1;
          break;
        case arrowCodes.LEFT:
          x = x - 1;
          break;
        case arrowCodes.RIGHT:
          x = x + 1;
          break;
      }

      x = app.sfp(x / app.ASSET_EDITOR_WIDTH);
      y = app.sfp(y / app.ASSET_EDITOR_HEIGHT);
      this.offsetX = x;
      this.offsetY = y;

      const value = `${x} ${y} ${this.width} ${this.height} ${this.zIndex}`;
      const valueChanges = {
        name: this.area.name,
        type: this.area.type,
        value,
      };
      this.updateField(valueChanges);
    },

    updateField(valueChanges, makeDirty = true) {
      this.ADD_OR_UPDATE_CUSTOM_OVERLAY_FIELD(valueChanges);
      if (makeDirty) {
        this.setTmpPageState({
          userInteractedWithPage: true,
        });
      }
    },
  },
};
</script>
